// Bump this only when the policy itself changes. It must be a fixed date, not
// generated at render time — a user who agreed to this policy needs a version we
// can point back to later, and a date that silently advances every day implies
// the policy changed when it did not. Matches the constant in terms-of-service.tsx.
const EFFECTIVE_DATE = "August 13, 2026";

export default function PrivacyPolicy() {
  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-sm mt-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Privacy Policy</h1>

      <div className="space-y-6 text-gray-700 leading-relaxed">
        <p><strong>Effective Date:</strong> {EFFECTIVE_DATE}</p>
        
        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>
          <p>
            FreshOrder ("we," "our," or "us") collects information you provide directly to us, such as when you:
          </p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Create an account or place orders through our platform</li>
            <li>Contact us for customer support</li>
            <li>Use our QuickBooks integration features</li>
          </ul>
          
          <h3 className="text-lg font-medium text-gray-900 mt-4 mb-2">Information collected includes:</h3>
          <ul className="list-disc ml-6 space-y-1">
            <li><strong>Contact Information:</strong> Name, phone number, business name, address</li>
            <li><strong>Order Information:</strong> Product selections, quantities, order notes, delivery details</li>
            <li><strong>QuickBooks Data:</strong> When you connect QuickBooks, we access invoice and customer data as authorized</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Process and fulfill your orders</li>
            <li>Manage your account and provide customer support</li>
            <li>Generate invoices and packing slips</li>
            <li>Integrate with your QuickBooks account for invoice management</li>
            <li>Improve our services and platform functionality</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Information Sharing</h2>
          <p>
            We do not sell, trade, or otherwise transfer your personal information to third parties except:
          </p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>With your explicit consent</li>
            <li>To fulfill orders (sharing delivery information with logistics partners)</li>
            <li>When using QuickBooks integration (data is shared with Intuit QuickBooks as authorized by you)</li>
            <li>To comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. QuickBooks Integration</h2>
          <p>
            When you connect your QuickBooks account:
          </p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>We access your QuickBooks data only as authorized by you during the OAuth process</li>
            <li>We may retrieve customer information and invoice history to provide pricing consistency</li>
            <li>We create invoice drafts in your QuickBooks account based on your orders</li>
            <li>You can disconnect your QuickBooks account at any time from the Settings page</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
          <p>
            We implement appropriate security measures to protect your personal information against unauthorized access, 
            alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
          <p>
            We retain your information for as long as necessary to provide our services and fulfill the purposes 
            outlined in this policy, unless a longer retention period is required by law.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Rights</h2>
          <p>You have the right to:</p>
          <ul className="list-disc ml-6 mt-2 space-y-1">
            <li>Access and update your personal information</li>
            <li>Request deletion of your account and associated data</li>
            <li>Disconnect third-party integrations like QuickBooks</li>
            <li>Contact us with privacy-related questions or concerns</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Contact Information</h2>
          <p>
            If you have any questions about this Privacy Policy, please contact us at:
          </p>
          <div className="mt-2">
            <p><strong>FreshOrder</strong></p>
            {/* support@freshorder.io is the intended address but the mailbox does not exist yet.
                Point at hello@ until it does — a privacy contact that bounces is worse than a
                generic one that works. Switch back once support@ is live. */}
            <p>Email: hello@freshorder.io</p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
            the new policy on this page with an updated effective date.
          </p>
        </section>
      </div>
    </div>
  );
}
